import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchSurveyByToken, submitSurvey, type SurveyDto, type AnswerDto, type QuestionDto } from '../api/surveyApi';
import { useIsMobile } from '../hooks/useIsMobile';

// ─── Anonymity statement ─────────────────────────────────────────────
// Always shown beneath the landing page message regardless of edits (§10).
const ANONYMITY_STATEMENT =
  'Your survey link is unique to you so we can track overall response rates by team — ' +
  'but your identity is never stored with your answers. ' +
  'Once you submit, your responses are completely anonymous.';

// ─── Merge-field substitution ─────────────────────────────────────────
function applyMergeFields(template: string, surveyTitle: string, expiresAt: string | null): string {
  const closeDate = expiresAt
    ? new Date(expiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : 'the closing date';
  return template
    .replace(/\[Survey Name\]/g, surveyTitle)
    .replace(/\[Close Date\]/g, closeDate);
}

// ─── Scale endpoints ─────────────────────────────────────────────────
const SCALE_LOW  = 'Not at all';
const SCALE_HIGH = 'Completely';

// ─── Sub-components ───────────────────────────────────────────────────

function ProgressBar({ current, total }: { current: number; total: number }) {
  const pct = total === 0 ? 0 : Math.round((current / total) * 100);
  return (
    <div className="wizard-header__top">
      <img src="/gridworks-logo.png" alt="GridPulse" className="wizard-logo" />
      <span className="wizard-step-label">{current} of {total}</span>
      <div className="wizard-progress-track" style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
        <div className="wizard-progress-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ScaleTiles({
  questionId,
  value,
  onChange,
}: {
  questionId: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <>
      <div className="scale-group" role="radiogroup">
        {[1, 2, 3, 4, 5].map(n => {
          const s = String(n);
          const selected = value === s;
          return (
            <label
              key={n}
              className={`scale-tile${selected ? ' scale-tile--selected' : ''}`}
            >
              <input
                type="radio"
                className="sr-only"
                name={questionId}
                value={s}
                checked={selected}
                onChange={() => onChange(s)}
              />
              {n}
            </label>
          );
        })}
      </div>
      <div className="scale-endpoints">
        <span>{SCALE_LOW}</span>
        <span>{SCALE_HIGH}</span>
      </div>
    </>
  );
}

function QuestionCard({
  question,
  index,
  total,
  value,
  onChange,
  showIndex,
}: {
  question: QuestionDto;
  index: number;
  total: number;
  value: string;
  onChange: (v: string) => void;
  showIndex: boolean;
}) {
  return (
    <div className="question-card">
      <p className="question-text">
        {showIndex && <span style={{ color: 'var(--color-text-muted)', fontWeight: 400, fontSize: '0.875rem' }}>
          {index + 1}/{total}&ensp;
        </span>}
        {question.text}
      </p>

      {question.type === 'Scale' && (
        <ScaleTiles questionId={question.questionId} value={value} onChange={onChange} />
      )}

      {question.type === 'FreeText' && (
        <textarea
          className="question-textarea"
          rows={4}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Your thoughts… (optional)"
        />
      )}
    </div>
  );
}

// ─── Intro screen (mobile step 0) ────────────────────────────────────
function IntroScreen({
  survey,
  onStart,
}: {
  survey: SurveyDto;
  onStart: () => void;
}) {
  const message = applyMergeFields(survey.landingPageMessage, survey.title, survey.expiresAt);

  return (
    <div className="wizard-body" style={{ paddingBottom: '6rem' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>{survey.title}</h1>

      <p style={{ lineHeight: 1.7, marginBottom: '1.25rem' }}>{message}</p>

      <div className="anonymity-banner" role="note" aria-label="Anonymity statement">
        <strong>Your anonymity is guaranteed</strong>
        <p style={{ margin: '0.625rem 0 0', lineHeight: 1.7 }}>
          {ANONYMITY_STATEMENT}
        </p>
      </div>

      <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
        {survey.questions.length} questions · takes about 2 minutes
      </p>
    </div>
  );
}

// ─── Mobile wizard ────────────────────────────────────────────────────
function MobileWizard({
  survey,
  token,
}: {
  survey: SurveyDto;
  token: string;
}) {
  const navigate = useNavigate();
  // step -1 = intro, 0..N-1 = questions, N = submitting
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const questions = survey.questions;
  const total = questions.length;
  const current = step + 1;           // progress display (0 on intro, 1..N on questions)
  const question = step >= 0 ? questions[step] : null;
  const answer = question ? (answers[question.questionId] ?? '') : '';
  const isLastQuestion = step === total - 1;
  const isOptional = question?.type === 'FreeText';

  const scrollTop = () => bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

  const handleAnswer = (v: string) => {
    if (!question) return;
    setAnswers(prev => ({ ...prev, [question.questionId]: v }));
    setError(null);
  };

  const goNext = async () => {
    if (step === -1) { setStep(0); scrollTop(); return; }

    if (!isOptional && !answer) {
      setError('Please select an answer to continue.');
      return;
    }
    setError(null);

    if (!isLastQuestion) { setStep(s => s + 1); scrollTop(); return; }

    // Submit
    setSubmitting(true);
    const payload: AnswerDto[] = Object.entries(answers).map(([questionId, value]) => ({
      questionId,
      value,
    }));
    try {
      await submitSurvey(token, payload);
      navigate('/survey/complete');
    } catch {
      setSubmitting(false);
      setError('Something went wrong. Please try again.');
    }
  };

  const goBack = () => {
    setError(null);
    setStep(s => s - 1);
    scrollTop();
  };

  return (
    <div className="wizard-root">
      <header className="wizard-header" style={{ position: 'relative' }}>
        <div className="wizard-header__top">
          <img src="/gridworks-logo.png" alt="GridPulse" className="wizard-logo" />
          {step >= 0 && (
            <span className="wizard-step-label">Question {current} of {total}</span>
          )}
        </div>
        <div className="wizard-progress-track">
          <div
            className="wizard-progress-fill"
            style={{ width: step < 0 ? '0%' : `${Math.round((current / total) * 100)}%` }}
          />
        </div>
      </header>

      <div className="wizard-body" ref={bodyRef} style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 120px)' }}>
        {step === -1 && <IntroScreen survey={survey} onStart={goNext} />}

        {question && (
          <QuestionCard
            question={question}
            index={step}
            total={total}
            value={answer}
            onChange={handleAnswer}
            showIndex={false}
          />
        )}

        {error && (
          <p role="alert" style={{ color: 'var(--color-error)', marginTop: '0.75rem', fontSize: '0.9rem' }}>
            {error}
          </p>
        )}
      </div>

      <footer className="wizard-footer">
        {step > -1 && (
          <button className="btn btn-ghost" onClick={goBack} disabled={submitting}>
            ← Back
          </button>
        )}
        <button
          className="btn btn-primary btn-block"
          onClick={goNext}
          disabled={submitting}
        >
          {submitting ? 'Submitting…' : step === -1 ? 'Get started' : isLastQuestion ? 'Submit response' : 'Next →'}
        </button>
      </footer>
    </div>
  );
}

// ─── Desktop layout (all questions, one scroll) ───────────────────────
function DesktopForm({
  survey,
  token,
}: {
  survey: SurveyDto;
  token: string;
}) {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bannerOpen, setBannerOpen] = useState(true);

  const handleAnswer = (questionId: string, v: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: v }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const unanswered = survey.questions.find(
      q => q.type !== 'FreeText' && !answers[q.questionId]
    );
    if (unanswered) {
      setError('Please answer all required questions before submitting.');
      return;
    }
    setError(null);
    setSubmitting(true);
    const payload: AnswerDto[] = Object.entries(answers).map(([questionId, value]) => ({
      questionId,
      value,
    }));
    try {
      await submitSurvey(token, payload);
      navigate('/survey/complete');
    } catch {
      setSubmitting(false);
      setError('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="wizard-root wizard-root--desktop">
      <header className="wizard-header">
        <div className="wizard-header__top">
          <img src="/gridworks-logo.png" alt="GridPulse" className="wizard-logo" />
        </div>
      </header>

      <div className="wizard-body">
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.5rem' }}>{survey.title}</h1>

        <p style={{ lineHeight: 1.7, marginBottom: '1.25rem' }}>
          {applyMergeFields(survey.landingPageMessage, survey.title, survey.expiresAt)}
        </p>

        <div className="anonymity-banner" role="note">
          <strong>Your anonymity is guaranteed</strong>
          {bannerOpen && (
            <p style={{ margin: '0.5rem 0 0', lineHeight: 1.7 }}>
              {ANONYMITY_STATEMENT}
            </p>
          )}
          <button className="anonymity-banner__toggle" onClick={() => setBannerOpen(o => !o)}>
            {bannerOpen ? 'Hide details' : 'Read privacy details'}
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {survey.questions.map((q, i) => (
            <div key={q.questionId} style={{ marginBottom: '1.25rem' }}>
              <QuestionCard
                question={q}
                index={i}
                total={survey.questions.length}
                value={answers[q.questionId] ?? ''}
                onChange={v => handleAnswer(q.questionId, v)}
                showIndex={true}
              />
            </div>
          ))}

          {error && (
            <p role="alert" style={{ color: 'var(--color-error)', marginBottom: '1rem' }}>
              {error}
            </p>
          )}

          <div className="wizard-footer">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit response'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Root component ───────────────────────────────────────────────────
export function TakeSurvey() {
  const { token } = useParams<{ token: string }>();
  const isMobile = useIsMobile();

  const [survey, setSurvey] = useState<SurveyDto | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'invalid'>('loading');

  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }
    fetchSurveyByToken(token)
      .then(data => { setSurvey(data); setStatus('ready'); })
      .catch(() => setStatus('invalid'));
  }, [token]);

  if (status === 'loading') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <p style={{ color: 'var(--color-text-muted)' }}>Loading your survey…</p>
      </div>
    );
  }

  if (status === 'invalid' || !token) {
    return (
      <div style={{ maxWidth: 480, margin: '4rem auto', padding: '0 1.5rem', textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Link unavailable</h1>
        <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
          This survey link is invalid, has already been used, or has expired.
          Contact HR if you believe this is an error.
        </p>
      </div>
    );
  }

  return isMobile
    ? <MobileWizard survey={survey!} token={token} />
    : <DesktopForm  survey={survey!} token={token} />;
}
